import { useCallback } from "react";
import { createVideoUrlBuilder } from "@/shared/helpers";
import { streamsApiInstance } from "../api/axios";

export function useVideoSourceGenerator({
  timeshift = 0,
  params,
}: {
  timeshift: number;
  params: {
    cameraId: string | null;
    fromTime: string;
    toTime: string;
    isLive: boolean;
    alignSegment: boolean;
    availabilityStartTime: number | null;
    computedAvailabilityEndTime: number | null;
    arbitraryFromTime: number | null;
    arbitraryToTime: number | null;
  };
}) {
  const {
    isLive,
    fromTime,
    toTime,
    alignSegment,
    cameraId,
    arbitraryFromTime,
    arbitraryToTime,
  } = params;

  const getLiveFromTime = useCallback(() => {
    return new Date(Date.now() - timeshift * 1000).toISOString();
  }, [timeshift]);

  const getVideoUrl = useCallback(() => {
    const videoUrl = createVideoUrlBuilder(streamsApiInstance.defaults.baseURL)
      .setIsLive(isLive)
      .setCameraId(cameraId)
      .setAlignSegment(alignSegment)
      .setTimeshift(timeshift);

    if (isLive) {
      videoUrl.setFromTime(getLiveFromTime());
    } else if (fromTime && toTime) {
      videoUrl.setFromTime(fromTime);
      videoUrl.setToTime(toTime);
      videoUrl.setAlignSegment(true);
    } else if (arbitraryFromTime && arbitraryToTime) {
      videoUrl.setFromTime(new Date(arbitraryFromTime).toISOString());
      videoUrl.setToTime(new Date(arbitraryToTime).toISOString());
    }

    return videoUrl.build();
  }, [
    timeshift,
    isLive,
    fromTime,
    toTime,
    alignSegment,
    cameraId,
    arbitraryFromTime,
    arbitraryToTime,
    getLiveFromTime,
  ]);

  return getVideoUrl;
}
